import { useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useFetchCustomerDetails } from '@zet/customer'
import { useAgentDetailsWF } from '@zet/customer/src/api/hooks/webFlow'
import { AgentDetails } from '@zet/customer/src/api/types'
import { useBrandRecommendations } from '@zet/feature-brands/hooks/useBrandRecommendations'
import { zetApi } from '@zet/network'
import { ONE_DAY } from '@zet/utils/cacheTime'
import {
  getUserFlowSession,
  UserFlowMode,
  UserFlowRouteName,
} from '@zet/utils/userFlowSession'

type UserFlowProduct = {
  id?: number
  partnerId?: number
  name?: string
  imageUrl?: string
  leadExists?: boolean
  payout?: {
    amount?: number
    type?: 'flat' | 'percentage'
  }
  benefits?: Array<{
    heading?: string
    subText?: string[]
  }>
}

export type UserFlowPartnerProduct = {
  id?: number
  partnerId?: number
  name?: string
  imageUrl?: string
  backgroundGradient?: string[]
}

export type UserFlowCustomerLeadStatus =
  | 'leadadded'
  | 'actnreqrd'
  | 'expired'
  | 'rejected'
  | 'fullfilled'
  | 'fulfilled'

export type UserFlowCustomerLead = {
  partnerId: number
  status: UserFlowCustomerLeadStatus
  createdDate: string
  currentStatusTitle: string
  currentStatusMessage: string
}

const getUserFlowMode = (agentData?: AgentDetails): UserFlowMode =>
  agentData?.customer?.customerId ? 'returning' : 'new'

const getUserFlowInitialValues = (agentData?: AgentDetails) => ({
  fullName: agentData?.customer?.fullName || '',
  mobileNumber: agentData?.customer?.phoneNumber?.toString() || '',
  email: agentData?.customer?.email || '',
})

const getUserFlowCustomerId = (fallbackCustomerId?: number) =>
  Number(fallbackCustomerId || getUserFlowSession()?.customerId || 0)

const getUserFlowFallbackRoute = (mode: UserFlowMode): UserFlowRouteName =>
  'UserHome'

const userFlowPartnerCategoryId = 9

const fetchUserFlowPartnerProducts = async () => {
  const { data } = await zetApi.get('/variants/products', {
    params: {
      categoryId: userFlowPartnerCategoryId,
      limit: 1000,
      page: 1,
      showIneligible: false,
      showMagnetCard: false,
    },
  })

  return (data?.data?.products || []) as UserFlowPartnerProduct[]
}

const dedupeProducts = (products: UserFlowProduct[]) => {
  const seen = new Set<string>()

  return products?.filter((product) => {
    const key = `${product?.partnerId || ''}-${product?.id || ''}`

    if (seen.has(key)) {
      return false
    }

    seen.add(key)
    return true
  })
}

const useUserFlowRecommendations = (customerId?: number) => {
  const { data: agentData } = useAgentDetailsWF()
  const resolvedCustomerId = getUserFlowCustomerId(customerId)
  const categoryId = 9
  const variantId = agentData?.variantId

  const recommendationQuery = useBrandRecommendations({
    categoryId,
    customerId: resolvedCustomerId,
    variantId,
    useFilters: false,
  })

  const { data: customerData } = useFetchCustomerDetails(resolvedCustomerId)
  const currentListings =
    recommendationQuery?.data?.pages?.[
      (recommendationQuery?.data?.pages?.length || 1) - 1
    ]?.data

  const allListings =
    recommendationQuery?.data?.pages
      ?.map((page) => page?.data)
      .filter(Boolean) || []

  const products = useMemo(() => {
    const items = allListings.flatMap((listing) => [
      ...(listing?.products || []),
    ])

    return dedupeProducts(items)
  }, [allListings])

  const customerLeads = useMemo(
    () => (currentListings?.customerLeads || []) as UserFlowCustomerLead[],
    [currentListings],
  )

  const freshProducts = useMemo(
    () => products.filter((product) => !product?.leadExists),
    [products],
  )

  const fetchNextPage = useCallback(() => {
    return recommendationQuery.fetchNextPage()
  }, [recommendationQuery])

  return {
    ...recommendationQuery,
    agentData,
    customerData,
    customerId: resolvedCustomerId,
    categoryId,
    customerLeads,
    products,
    freshProducts,
    totalProductCount:
      currentListings?.totalProductCount || freshProducts.length,
    paginationMeta: currentListings?.paginationMeta,
    hasNextPage: recommendationQuery.hasNextPage,
    isFetchingNextPage: recommendationQuery.isFetchingNextPage,
    fetchNextPage,
  }
}

const useUserFlowPartnerProducts = (enabled = true) =>
  useQuery({
    queryKey: ['userFlowPartnerProducts', userFlowPartnerCategoryId],
    queryFn: fetchUserFlowPartnerProducts,
    staleTime: ONE_DAY,
    cacheTime: ONE_DAY,
    enabled,
  })

export {
  fetchUserFlowPartnerProducts,
  getUserFlowCustomerId,
  getUserFlowFallbackRoute,
  getUserFlowInitialValues,
  getUserFlowMode,
  useUserFlowPartnerProducts,
  useUserFlowRecommendations,
}
